/**
 * CRM operator guide — plain English copy for VIVA Team (non-technical).
 */

import { LEAD_SOURCE_LABELS, LEAD_STATUS_LABELS, CRM_HELP_SOURCE_ORDER } from '../lib/crm-display'

export const CRM_HELP_INTRO = {
  title: 'CRM guide for the VIVA Team',
  summary:
    'This app replaces the Google Sheet for people who contacted VIVA but have not had their first appointment yet. Use it to see every case in one place, add notes after each call or message, track status, enter intake form data yourself when the client gave answers by phone, and copy intake form fields into Simple Practice when you are ready.',
  replaces:
    'The old Contact and Client Intake apps in VIVA HUB now redirect here. Website forms still work the same for clients — new submissions appear automatically as cases.',
}

export const CRM_QUICK_START = [
  {
    title: 'Open the case list',
    body: 'Go to VIVA HUB → CRM. You will see all active cases with status, source, assignee, and how many days since the last update.',
  },
  {
    title: 'Pick up or create a case',
    body: 'Click a name to open the case file. For contacts not on the list yet, click New case, enter name, phone, and/or email, and choose the source (WhatsApp, Phone, Psychology Today, Instagram, Referral, or Other).',
  },
  {
    title: 'Work the case file',
    body: 'Add a note after every interaction, update status as you move forward, assign yourself or a teammate, and copy intake form fields into Simple Practice when scheduling is done.',
  },
  {
    title: 'Fill intake form by phone or in person',
    body: 'If the client already gave you intake form details on a call (no website form), open the case and click Intake form. Same fields as the public form — nothing is emailed to the client.',
  },
  {
    title: 'Finish in Simple Practice',
    body: 'After the first appointment is booked and the client is created in Simple Practice, click Migrated to Simple Practice on the case file. The CRM record becomes read-only history.',
  },
]

export const CRM_STATUS_PIPELINE = [
  'new',
  'contacted',
  'intake_sent',
  'intake_received',
  'scheduling',
  'scheduled',
  'in_simple_practice',
]

export const CRM_STATUS_DESCRIPTIONS = [
  {
    status: 'new',
    meaning: 'Just arrived — website form, WhatsApp, or phone. No one has reached out yet.',
    youDo: 'Contact the person and move to Contacted. Assign an owner if helpful.',
  },
  {
    status: 'contacted',
    meaning: 'Someone on the team has responded (message, email, or call).',
    youDo: 'Send the intake form link if needed → Intake form sent. Add notes summarizing the conversation.',
  },
  {
    status: 'intake_sent',
    meaning: 'Intake form link was sent; waiting for them to submit.',
    youDo: 'Follow up if they are slow. When the form arrives, status becomes Intake form received automatically.',
  },
  {
    status: 'intake_received',
    meaning:
      'Intake form is on file — from the website or entered manually by the team after a phone or in-person conversation. Latest answers show under Latest intake form.',
    youDo: 'Review the intake form, call to schedule → Scheduling.',
  },
  {
    status: 'scheduling',
    meaning: 'Working on finding a time for the first session.',
    youDo: 'When the appointment is confirmed → Scheduled. Copy the intake form into Simple Practice while scheduling.',
  },
  {
    status: 'scheduled',
    meaning: 'First appointment is on the calendar.',
    youDo: 'Create the client in Simple Practice, then mark Migrated to Simple Practice in CRM.',
  },
  {
    status: 'in_simple_practice',
    meaning: 'Done in CRM — clinical work continues only in Simple Practice.',
    youDo: 'No further CRM updates needed. File is read-only.',
  },
  {
    status: 'on_hold',
    meaning: 'Paused — waiting on the client or an external factor.',
    youDo: 'Add a note explaining why. Resume to Contacted or the right step when ready.',
  },
  {
    status: 'lost',
    meaning: 'Did not move forward (no response, chose another provider, etc.).',
    youDo: 'Add a short note. Record auto-archives after 30 days.',
  },
  {
    status: 'not_interested',
    meaning: 'Declined services.',
    youDo: 'Same as Lost — note and archive.',
  },
]

export const CRM_LIST_GUIDE = {
  title: 'Case list — filters and columns',
  filters: [
    { name: 'My cases / Unassigned / All', detail: 'My cases shows only cases assigned to you. Unassigned needs a team member to claim them.' },
    { name: 'Status', detail: 'Narrow the list to one stage (e.g. Intake form sent).' },
    {
      name: 'Source',
      detail:
        'WhatsApp, Phone, Psychology Today, Instagram, Referral, Other, or website forms (Contact, Book session, Intake form).',
    },
    { name: 'Search', detail: 'Name, email, or phone — partial match works.' },
    { name: 'Idle only', detail: 'Cases with no activity longer than the threshold for their status (highlighted in amber).' },
    {
      name: 'Has intake form',
      detail: 'Only cases with an intake form on file (website submission or manual entry).',
    },
    { name: 'Include archived', detail: 'Shows old Lost / Not interested cases after auto-archive.' },
  ],
  columns: [
    { name: 'Name', detail: 'Click to open the full case file.' },
    { name: 'Status', detail: 'Current step in the process (see status guide below).' },
    { name: 'Assignee', detail: 'Team member responsible. Empty = Unassigned.' },
    { name: 'Idle', detail: 'Days since last note, status change, or form — not since you merely opened the file.' },
  ],
}

export const CRM_LEAD_FILE_GUIDE = {
  title: 'Case file — what each section does',
  sections: [
    {
      name: 'Header (status & assignee)',
      detail: 'Change status from the dropdown (only allowed next steps appear). Assignee lists CRM Assignee group members — set in Hub → Users.',
    },
    {
      name: 'Timeline',
      detail: 'Every note, status change, and form submission in order. Always add a note after a call or WhatsApp exchange so the next person has context.',
    },
    {
      name: 'Client',
      detail: 'For minors: guardian + child. For couples: both partners. For adults: one person.',
    },
    {
      name: 'Latest intake form',
      detail:
        'Copy fields into Simple Practice. Website submissions appear automatically. Use Intake form when the client gave details by phone or in person — no email is sent to the client. A new manual entry replaces the latest intake form on this file; older submissions stay in the timeline only.',
    },
    {
      name: 'Migrated to Simple Practice',
      detail: 'Use only after the client exists in SP. Optional reference field (SP client ID or note). After migration the case is read-only.',
    },
  ],
}

export const CRM_MANUAL_INTAKE_GUIDE = {
  title: 'Intake form (phone or in person)',
  intro:
    'Use this when a VIVA Team member already collected the same information as the website Client Intake form — for example the client read their details over the phone and you typed them into the Intake form. You do not need to ask them to submit the public form.',
  whenToUse: [
    'The client gave intake form answers on a call, video call, or at the front desk.',
    'You already created the case manually (WhatsApp, phone, Psychology Today, Instagram, referral, etc.) and now have their full intake form details.',
    'You want copyable fields on the case file for Simple Practice without waiting for a website submission.',
  ],
  whenNotToUse: [
    'The client can still fill the website form — send them the link and let status update automatically when they submit.',
    'The case is already Migrated to Simple Practice or archived — the file is read-only.',
  ],
  steps: [
    'Open the case in CRM (search by phone or email first to avoid duplicates).',
    'Click Intake form in the header, or Intake form in the sidebar if there is no intake form yet.',
    'Choose Adult, Minor, or Couple and the service requested (same options as the public form).',
    'Enter all required fields. Set Client communication language to English or Portuguese for future client emails — this does not change the CRM screen language.',
    'Click Save intake form. You return to the case file with Latest intake form filled in.',
    'Add a timeline note if helpful (e.g. “Intake form taken by phone, verified spelling”).',
    'Continue with Scheduling when ready; copy fields from Latest intake form into Simple Practice.',
  ],
  whatHappens: [
    'The intake form is saved on this case and shown under Latest intake form (with copy buttons).',
    'The Client section updates for minors (guardian + child) and couples (both partners).',
    'Status usually moves to Intake form received if the case was still at New, Contacted, or Intake form sent.',
    'The timeline records “Intake form entered manually”.',
    'No confirmation email is sent to the client.',
    'Saving again replaces Latest intake form; older entries remain in the timeline only.',
  ],
  buttons: [
    { name: 'Intake form', detail: 'On the case file header when there is no intake form yet, or to add the first one.' },
    {
      name: 'Update intake form / Replace with new entry',
      detail: 'When an intake form already exists — use if the client gave corrected or complete information. Replaces what you see under Latest intake form.',
    },
  ],
}

export const CRM_ASSIGNEE_GUIDE = {
  title: 'CRM supplementary groups',
  body: 'Use supplementary groups in Hub → Users (with a primary role: Admin, VIVA Associate, or Renter). CRM Assignee — appears in the Assignee dropdown and may own cases. CRM Notification — enable Email for that group under CRM Settings → Notification channels by group; those users receive case alerts. CRM Nudge — members can use “Nudge team member” on a case to email a friendly reminder to the assignee or another CRM team member (hub admins can always nudge). VIVA Associates are added to Assignee and Notification automatically on deploy; add CRM Nudge only for people who should send reminders.',
}

/** Visual workflows (from doc/diagrams) in plain steps */
export const CRM_WORKFLOWS = [
  {
    id: 'happy-path',
    title: 'Full path — client moves quickly (happy path)',
    subtitle: 'From first WhatsApp message to Simple Practice',
    steps: [
      'Client messages on WhatsApp',
      'Team creates or finds case in CRM (status: New)',
      'Team replies and sends intake form link → Contacted → Intake form sent',
      'Client submits the intake form on the website → Intake form received (automatic)',
      'Team calls to schedule → Scheduling → Scheduled',
      'Team creates client in Simple Practice and sends SP forms',
      'Team clicks Migrated to Simple Practice in CRM',
    ],
    note: 'Add a timeline note after each touchpoint so nothing lives only in WhatsApp.',
  },
  {
    id: 'slow-response',
    title: 'Full path — client is slow to respond',
    subtitle: 'Same journey with waiting and follow-up',
    steps: [
      'Case created; intake form link sent',
      'Days pass with no intake form — case shows as idle on the list',
      'Team follows up by phone or message; add a note in CRM',
      'Optional: set On hold if waiting on client',
      'When the intake form finally arrives → Intake form received',
      'Continue scheduling → Simple Practice → Migrated',
    ],
    note: 'Automatic email/WhatsApp reminders to the client are planned for a later phase. Until then, use the idle filter and follow up manually.',
  },
  {
    id: 'website-form',
    title: 'Website form (Contact, Book session, or Intake form)',
    steps: [
      'Client submits form on vivapsychotherapy.com',
      'CRM creates or updates one case (same phone or email = same case)',
      'Team gets email notification (existing process)',
      'Open case in CRM, assign owner, set Contacted',
      'If intake form: status may jump to Intake form received automatically',
      'Continue with calls and scheduling as usual',
    ],
    note: 'Contact and Book session usually start as New until someone reaches out. Intake form advances status when received.',
  },
  {
    id: 'whatsapp-entry',
    title: 'Manual entry — New case',
    steps: [
      'Client reaches out outside the website (message, call, Psychology Today, Instagram, referral, etc.)',
      'Search CRM by phone or email first (avoid duplicates)',
      'If new: New case — enter name, phone or email, and pick the matching source',
      'If existing: open case; CRM may reopen a closed case if they contact again',
      'Add note with conversation summary; update status',
      'Send intake form link when appropriate, or use Intake form if they already gave you all details on the call',
    ],
  },
  {
    id: 'manual-intake',
    title: 'Phone intake form — fill intake form (no website form)',
    subtitle: 'Client gave intake form answers verbally; team types them in',
    steps: [
      'Case exists in CRM (from call, WhatsApp, or contact form)',
      'On the call, collect the same information as the Client Intake form (adult, minor, or couple)',
      'Open the case → Intake form',
      'Fill the form → Save intake form',
      'Status becomes Intake form received; Latest intake form is ready to copy into Simple Practice',
      'Schedule first appointment → Scheduled → Migrated to Simple Practice',
    ],
    note: 'Use Intake form instead of asking the client to submit the website form when you already have everything. You can still send the public form later if something was missing — a new website submission would replace Latest intake form.',
  },
  {
    id: 'timeline-daily',
    title: 'Using the timeline every day',
    steps: [
      'Open case before calling or messaging',
      'Read recent notes and status',
      'After the interaction: add note (what was said, next step, language preference, etc.)',
      'Update status if the stage changed',
      'Change assignee if handing off to another team member',
    ],
    note: 'The timeline replaces scattered spreadsheet cells — never delete events; add new notes instead.',
  },
  {
    id: 'simple-practice',
    title: 'Moving to Simple Practice',
    steps: [
      'First appointment date confirmed → status Scheduled',
      'In Simple Practice: create client; copy fields from Latest intake form in CRM',
      'Send SP portal / forms to client from Simple Practice',
      'In CRM: optional SP reference (client ID); click Migrated to Simple Practice',
      'CRM file is archived for history; all clinical work stays in SP',
    ],
    note: 'There is no automatic sync with Simple Practice — copying is manual by design.',
  },
]

const CRM_SOURCE_HINTS = {
  whatsapp: 'New case — client messaged on WhatsApp. CRM matches existing cases by phone.',
  phone: 'New case — client called. CRM matches existing cases by phone.',
  psychology_today: 'New case — client found VIVA on Psychology Today.',
  instagram: 'New case — client reached out via Instagram.',
  referral: 'New case — referred by another client, doctor, or professional.',
  unknown: 'New case — source is Other or not listed above.',
  website_contact: 'Auto-created from the Contact Us form on the website.',
  website_book: 'Auto-created from the Book a session form on the website.',
  website_intake: 'Auto-created from the Client Intake form; often sets Intake form received.',
}

export const CRM_ENTRY_CHANNELS = CRM_HELP_SOURCE_ORDER.map((key) => ({
  key,
  label: LEAD_SOURCE_LABELS[key],
  hint: CRM_SOURCE_HINTS[key] || '',
}))

export const CRM_FAQ = [
  {
    q: 'Why do I see one case instead of two for the same person?',
    a: 'CRM matches phone OR email. WhatsApp, a website form, and a call from the same number attach to one timeline.',
  },
  {
    q: 'Someone contacted us again after we marked them Lost.',
    a: 'CRM usually reopens the case to Contacted and adds a timeline event. If they were already in Simple Practice, a new case may be created instead.',
  },
  {
    q: 'I do not see myself in Assignee.',
    a: 'Ask a Hub admin to add the CRM Assignee group on your user (Hub → Users). You still need a primary role with CRM app access (Admin or VIVA Associate).',
  },
  {
    q: 'What counts as “activity” for idle days?',
    a: 'Notes, status changes, assignments, and new form submissions. Opening the case without saving does not reset idle.',
  },
  {
    q: 'Can I edit a case after Simple Practice?',
    a: 'No — In Simple Practice is read-only. Hub admins can override status only in exceptional cases.',
  },
  {
    q: 'Where did Contact and Client Intake apps go?',
    a: 'They redirect to CRM with filters. All website forms still work; only the admin view changed.',
  },
  {
    q: 'When should I use Intake form instead of sending the website form?',
    a: 'Use Intake form when you already collected the answers on a call or in person. Send the public intake form link when the client still needs to submit online themselves.',
  },
  {
    q: 'Does the client get an email after manual intake form entry?',
    a: 'No. Manual entry is for staff only. Website submissions still send the usual confirmation email to the client.',
  },
  {
    q: 'I entered the intake form twice — which one counts?',
    a: 'Latest intake form on the case file always shows the most recent save (website or manual). Earlier submissions stay in the timeline for history.',
  },
]

export const CRM_COMING_SOON = [
  'Email / WhatsApp / call alerts when a case sits idle too long (team notification group)',
  'Automatic reminders to the client to complete the intake form',
]

export const CRM_HELP_SECTIONS = [
  { id: 'quick-start', label: 'Quick start' },
  { id: 'statuses', label: 'Case statuses' },
  { id: 'list', label: 'Case list' },
  { id: 'lead-file', label: 'Case file' },
  { id: 'manual-intake', label: 'Intake form' },
  { id: 'assignee', label: 'Assignee' },
  { id: 'workflows', label: 'Step-by-step workflows' },
  { id: 'sources', label: 'Where cases come from' },
  { id: 'faq', label: 'FAQ' },
  { id: 'coming-soon', label: 'Coming later' },
]

export function getStatusLabel(status) {
  return LEAD_STATUS_LABELS[status] || status
}
